import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import './BlogPost.css';

// Simple frontmatter parser for browser (no Buffer needed)
function parseFrontmatter(text) {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = text.match(frontmatterRegex);
  
  if (!match) {
    return { data: {}, content: text };
  }
  
  const frontmatter = match[1];
  const content = match[2];
  const data = {};
  
  // Parse YAML-like frontmatter (simple key: value pairs)
  // Handle values that may contain colons by splitting on first colon only
  frontmatter.split('\n').forEach(line => {
    const trimmedLine = line.trim();
    if (!trimmedLine) return; // Skip empty lines
    
    const colonIndex = trimmedLine.indexOf(':');
    if (colonIndex > 0) {
      const key = trimmedLine.substring(0, colonIndex).trim();
      let value = trimmedLine.substring(colonIndex + 1).trim();
      // Remove quotes if present
      value = value.replace(/^["']|["']$/g, '');
      data[key] = value;
    }
  });
  
  return { data, content };
}

function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        // Map of slugs to filenames
        const postMap = {
          'welcome-to-my-blog': 'welcome-to-my-blog.md',
          'building-a-career-map': 'building-a-career-map.md',
          'nyc-ferry-observations': 'nyc-ferry-observations.md'
        };

        const filename = postMap[slug];
        if (!filename) {
          setError('Post not found');
          setLoading(false);
          return;
        }

        // Use process.env.PUBLIC_URL for proper path resolution
        const baseUrl = process.env.PUBLIC_URL || '';
        const response = await fetch(`${baseUrl}/posts/${filename}`);
        if (!response.ok) {
          setError('Post not found');
          setLoading(false);
          return;
        }

        const text = await response.text();
        const { data, content } = parseFrontmatter(text);

        setPost({
          ...data,
          content,
          date: new Date(data.date)
        });
        setLoading(false);
      } catch (err) {
        console.error('Error loading post:', err);
        setError('Failed to load post');
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-post-container">
        <div className="blog-loading">Loading post...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-container">
        <div className="blog-error">
          <p>{error || 'Post not found'}</p>
          <Link to="/blog" className="back-to-blog">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-container">
      <div className="blog-post-nav">
        <button onClick={() => navigate('/blog')} className="back-button">
          ← Back to Blog
        </button>
      </div>

      <article className="blog-post">
        <header className="blog-post-header">
          <h1 className="blog-post-title">{post.title}</h1>
          <div className="blog-post-meta">
            <time className="post-date">{formatDate(post.date)}</time>
          </div>
        </header>

        <div className="blog-post-content">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>
      </article>

      <div className="blog-post-footer">
        <Link to="/blog" className="back-to-blog-link">
          ← Back to Blog
        </Link>
      </div>
    </div>
  );
}

export default BlogPost;

